import { useQuery } from '@tanstack/react-query';
import { Bookmark, Mic, Sparkles, Square } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { glossaryMatchers, plainText, spotTerms, type ExplainResult, type GlossaryEntry, type ModuleContext, type NowContext } from '@tempo/core';
import { api, ApiError } from '../../api';
import { time } from '../../lib/format';
import { useClaudeStatus, useSettings } from '../../lib/hooks';
import { useSpeech } from '../../lib/speech';
import { useWakeLock } from '../../lib/device';
import { toast } from '../../lib/toast';
import { Button } from '../ui';

interface Segment {
  at: number;
  text: string;
}

interface ContextResponse {
  available: boolean;
  vault: string | null;
  module: ModuleContext | null;
  glossary: GlossaryEntry[];
}

export function useLectureContext(module: string | null) {
  return useQuery({
    queryKey: ['assistant-context', module],
    queryFn: () => api.get<ContextResponse>(`/assistant/context${module ? `?module=${encodeURIComponent(module)}` : ''}`),
    staleTime: 10 * 60_000,
  });
}

/** Live transcript → glossary terms (instant, offline) + Claude explanations every couple of minutes. */
export function LiveNotes({ n }: { n: NowContext }) {
  const item = n.item!;
  const module = n.module?.code ?? null;
  const settings = useSettings().data;
  const assistant = settings?.assistant;
  const claude = useClaudeStatus();
  const claudeOn = !!claude.data?.ready;
  const ctx = useLectureContext(module);
  const matchers = useMemo(() => glossaryMatchers(ctx.data?.glossary ?? []), [ctx.data]);

  const [segments, setSegments] = useState<Segment[]>([]);
  const segRef = useRef<Segment[]>([]);
  segRef.current = segments;
  const speech = useSpeech({ lang: 'en-GB', onFinal: (text) => setSegments((s) => [...s, { at: Date.now(), text }]) });
  useWakeLock(speech.listening);

  // Glossary terms heard so far, most recent first.
  const spotted = useMemo(() => spotTerms(segments, matchers), [segments, matchers]);

  // Claude explanations on a timer, only when there's enough new speech.
  const [explanations, setExplanations] = useState<ExplainResult[]>([]);
  const [explaining, setExplaining] = useState(false);
  const [explainError, setExplainError] = useState<string | null>(null);
  const explainedUpTo = useRef(0);
  const lastSummary = useRef<string | null>(null);
  useEffect(() => {
    if (!speech.listening || !claudeOn) return;
    const every = Math.max(30, assistant?.explainIntervalSec ?? 120) * 1000;
    const timer = window.setInterval(async () => {
      const segs = segRef.current;
      // Wait for at least ~25 new words so silence or small talk costs nothing.
      const freshWords = segs
        .slice(explainedUpTo.current)
        .map((s) => s.text)
        .join(' ')
        .split(/\s+/)
        .filter(Boolean).length;
      if (freshWords < 25) return;
      const recent = segs.filter((s) => s.at > Date.now() - 3 * 60_000).map((s) => s.text).join(' ');
      explainedUpTo.current = segs.length;
      setExplaining(true);
      try {
        const r = await api.post<ExplainResult>('/assistant/explain', { module, title: item.title, transcript: recent, previous: lastSummary.current });
        lastSummary.current = r.summary;
        setExplanations((e) => [r, ...e].slice(0, 6));
        setExplainError(null);
      } catch (err) {
        // Not signed in / PC unreachable: the status line below says what to do.
        if (err instanceof ApiError && (err.status === 409 || err.status === 503)) void claude.refetch();
        else if (!(err instanceof ApiError && err.status === 429)) setExplainError((err as Error).message);
      } finally {
        setExplaining(false);
      }
    }, every);
    return () => window.clearInterval(timer);
  }, [speech.listening, claudeOn, assistant?.explainIntervalSec, module, item.title]);

  // Optional: keep the transcript on the server.
  useEffect(() => {
    if (!assistant?.keepTranscripts || segments.length === 0) return;
    const t = window.setTimeout(() => {
      void api
        .put(`/assistant/transcripts/${encodeURIComponent(item.id)}`, {
          text: segments.map((s) => `[${time(s.at)}] ${s.text}`).join('\n'),
          module,
          title: item.title,
          startedAt: segments[0].at,
        })
        .catch(() => undefined);
    }, 20_000);
    return () => window.clearTimeout(t);
  }, [segments, assistant?.keepTranscripts, item.id, item.title, module]);

  const [marks, setMarks] = useState(0);
  const mark = async () => {
    const excerpt = segRef.current
      .filter((s) => s.at > Date.now() - 30_000)
      .map((s) => s.text)
      .join(' ');
    try {
      await api.post('/assistant/marks', { eventId: item.id, module, title: item.title, at: Date.now(), sessionStart: item.start, excerpt: excerpt || null });
      setMarks((m) => m + 1);
      toast(`Marked ${time(Date.now())} – it'll line up with the recording`);
    } catch (err) {
      toast((err as Error).message, 'error');
    }
  };

  const recentLines = segments.slice(-6);
  return (
    <div className="stack">
      <div className="row wrap" style={{ gap: 8 }}>
        {speech.supported ? (
          speech.listening ? (
            <Button onClick={speech.stop}>
              <Square size={15} /> Stop
            </Button>
          ) : (
            <Button variant="primary" onClick={speech.start}>
              <Mic size={16} /> {segments.length ? 'Resume transcript' : 'Start live transcript'}
            </Button>
          )
        ) : null}
        <Button onClick={mark}>
          <Bookmark size={16} /> Mark this moment{marks ? ` (${marks})` : ''}
        </Button>
        {speech.listening && (
          <span className="live-dot">
            <i /> Transcribing
          </span>
        )}
        {explaining && (
          <span className="faint small">
            <Sparkles size={13} /> explaining…
          </span>
        )}
      </div>
      {!speech.supported && <div className="callout small">Live transcripts need Chrome, Edge or Safari. Marks and the glossary still work.</div>}
      {speech.error && <div className="callout danger small">{speech.error}</div>}
      {explainError && <div className="callout danger small">{explainError}</div>}
      {claude.data && !claude.data.ready && speech.listening && <p className="tiny faint">{claude.data.message}</p>}

      {explanations.length > 0 && (
        <div className="explain-feed">
          {explanations.slice(0, 3).map((e, i) => (
            <div key={e.at} className={`explain ${i ? 'older' : ''}`}>
              <div className="explain-summary">{e.summary}</div>
              {e.concepts.map((c) => (
                <div key={c.term} className="concept">
                  <b>{c.term}</b> – {c.explanation}
                  {c.buildsOn.length > 0 && <div className="tiny faint">builds on: {c.buildsOn.join(', ')}</div>}
                </div>
              ))}
              {i === 0 && e.checkQuestion && <div className="tiny subtle">Check yourself: {e.checkQuestion}</div>}
            </div>
          ))}
        </div>
      )}

      {spotted.length > 0 && (
        <div className="terms">
          {spotted.map(({ entry }) => (
            <div key={entry.term} className="term">
              <div className="row" style={{ gap: 6 }}>
                <b>{entry.term}</b>
                <span className={`badge ${entry.status === 'learned' ? 'ok' : 'accent'}`}>{entry.status}</span>
              </div>
              <div className="small subtle">{plainText(entry.definition)}</div>
            </div>
          ))}
        </div>
      )}

      {(recentLines.length > 0 || speech.interim) && (
        <div className="transcript">
          {recentLines.map((s) => (
            <p key={s.at}>{s.text}</p>
          ))}
          {speech.interim && <p className="interim">{speech.interim}</p>}
        </div>
      )}

      <p className="tiny faint">
        Speech is turned into text by your browser's speech service, which may process audio online.{' '}
        {assistant?.keepTranscripts ? 'Transcripts are saved to Tempo.' : 'Nothing is saved.'} Check your uni's policy on recording lectures.
      </p>
    </div>
  );
}

/** What this module has covered recently, open work, and what's next. */
export function ModuleInfo({ n }: { n: NowContext }) {
  const m = n.module;
  if (!m || (!m.name && !m.recentLectures.length)) {
    return <p className="subtle">No notes for {m?.code ?? 'this module'} yet – they appear once School Manager shares its context.</p>;
  }
  return (
    <div className="stack">
      <div>
        <div className="a-big">{m.name ?? m.code}</div>
        {m.lecturer && <div className="subtle small">{m.lecturer}</div>}
      </div>
      {m.nextLectureHint && <div className="callout small">{m.nextLectureHint}</div>}
      {m.recentLectures.slice(0, 3).map((l) => (
        <div key={l.title} className="stack tight">
          <div>
            <b>{l.title}</b> <span className="faint small">{l.date}</span>
          </div>
          {l.summary && (
            <div className="small subtle">
              {plainText(l.summary).slice(0, 260)}
              {l.summary.length > 260 ? '…' : ''}
            </div>
          )}
          {l.keyConcepts.length > 0 && (
            <div className="chips">
              {l.keyConcepts.slice(0, 6).map((k) => (
                <span key={k} className="badge">
                  {k}
                </span>
              ))}
            </div>
          )}
        </div>
      ))}
      {m.openWork.length > 0 && (
        <div className="stack tight">
          <div className="a-label">Open work</div>
          {m.openWork.slice(0, 4).map((w) => (
            <div key={w.id} className="small">
              {w.title}
              {w.due ? <span className="faint"> · due {new Date(w.due).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span> : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** The module's terms from your vault. */
export function Terms({ n }: { n: NowContext }) {
  const ctx = useLectureContext(n.module?.code ?? null);
  const code = n.module?.code;
  const terms = (ctx.data?.glossary ?? []).filter((g) => !code || g.modules.includes(code));
  if (!terms.length) return <p className="subtle">No terms for this module yet.</p>;
  return (
    <div className="terms">
      {terms.slice(0, 40).map((g) => (
        <div key={g.term} className="term">
          <div className="row" style={{ gap: 6 }}>
            <b>{g.term}</b>
            <span className={`badge ${g.status === 'learned' ? 'ok' : 'accent'}`}>{g.status}</span>
          </div>
          <div className="small subtle">{plainText(g.definition)}</div>
        </div>
      ))}
    </div>
  );
}
